<template>
  <v-form>
    <v-dialog
      :model-value="show"
      width="500"
      persistent
      @update:model-value="$emit('update:show', $event)"
    >
      <v-card>
        <v-card-title class="text-h5 grey lighten-2 mb-4">
          DG Filters
        </v-card-title>

        <v-card-text>
          <!-- Location Search Section -->
          <v-expansion-panels v-model="locationPanel" class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
                  <span>Locatie zoeken</span>
                  <v-spacer></v-spacer>
                  <v-chip
                    v-if="isGeolocationActive"
                    color="success"
                    size="small"
                    class="mr-2"
                  >
                    Actief
                  </v-chip>
                </div>
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <!-- Google Places Location Input -->
                <Geocode
                  v-model="selectedLocation"
                  label="Zoek adres"
                  placeholder="Bijv: Stationsplein 1, Amsterdam"
                  :multiple="false"
                  :debug="false"
                  class="mb-3"
                  @selected="onLocationSelected"
                />

                <!-- GPS Location Button -->
                <v-btn
                  color="primary"
                  prepend-icon="mdi-crosshairs-gps"
                  variant="outlined"
                  class="mb-3"
                  :loading="gettingLocation"
                  @click="getCurrentLocation"
                >
                  Gebruik huidige locatie
                </v-btn>

                <!-- Current Location Display -->
                <div v-if="currentLocation" class="mb-3">
                  <v-chip
                    color="success"
                    prepend-icon="mdi-map-marker-check"
                    size="small"
                    class="mb-2"
                  >
                    {{ currentLocationName || "Locatie geselecteerd" }}
                  </v-chip>
                </div>

                <!-- Radius Selector -->
                <v-select
                  v-model="searchRadius"
                  :items="radiusOptions"
                  label="Zoekradius"
                  prepend-icon="mdi-radius"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                  :disabled="!currentLocation"
                ></v-select>

                <!-- Clear Location Button -->
                <v-btn
                  v-if="currentLocation"
                  color="warning"
                  prepend-icon="mdi-close"
                  variant="outlined"
                  size="small"
                  @click="clearLocationSearch"
                >
                  Locatie wissen
                </v-btn>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-select
            v-model="localValue.vip_eq"
            :items="[
              { text: '', value: '' },
              { text: 'Ja', value: 'true' },
              { text: 'Nee', value: 'false' },
            ]"
            item-title="text"
            item-value="value"
            label="VIP"
            prepend-icon="mdi-account-star"
            outlined
            dense
            @update:model-value="update_selected_options"
          ></v-select>

          <v-select
            v-model="localValue.gender_eq"
            :items="[
              { text: '', value: '' },
              { text: 'man', value: 'm' },
              { text: 'vrouw', value: 'v' },
            ]"
            item-title="text"
            item-value="value"
            label="Geslacht"
            prepend-icon="event"
            outlined
            dense
            @update:model-value="update_selected_options"
          ></v-select>

          <v-select
            v-model="localValue.mz_eq"
            :items="[
              { text: '', value: '' },
              { text: 'Ja', value: 'true' },
              { text: 'Nee', value: 'false' },
            ]"
            item-title="text"
            item-value="value"
            label="Complex"
            prepend-icon="fingerprint"
            outlined
            dense
            @update:model-value="update_selected_options"
          ></v-select>

          <v-select
            v-model="localValue.psychiatrisch_eq"
            :items="[
              { text: '', value: '' },
              { text: 'Ja', value: 'true' },
              { text: 'Nee', value: 'false' },
            ]"
            item-title="text"
            item-value="value"
            label="Psychiatrisch"
            prepend-icon="mdi-comment-alert-outline"
            outlined
            dense
            @update:model-value="update_selected_options"
          ></v-select>

          <v-autocomplete
            v-model="localValue.country_eq"
            :items="[{ name: '', short_name: '' }, ...countries]"
            label="Nationaliteit"
            item-title="name"
            item-value="short_name"
            prepend-icon="mdi-earth"
            outlined
            dense
            @update:model-value="update_selected_options"
          ></v-autocomplete>

          <v-select
            v-model="localValue.account_lead_id_eq"
            :items="[{ name: '', id: '' }, ...account_leads]"
            item-title="name"
            item-value="id"
            prepend-icon="mdi-magnify-plus"
            label="Aanleiding"
            outlined
            dense
            @update:model-value="update_selected_options"
          ></v-select>
          <v-select
            v-model="localValue.account_category_id_eq"
            :items="[{ name: '', id: '' }, ...account_categories]"
            item-title="name"
            item-value="id"
            prepend-icon="mdi-bed"
            label="Doelgroep"
            outlined
            dense
            @update:model-value="update_selected_options"
          ></v-select>

          <v-switch
            v-model="any_selected"
            :label="getLabelForSwitch()"
            @update:model-value="resetValuesForDeactivatedSelector"
          ></v-switch>

          <ReportTraitSelect
            v-if="any_selected"
            v-model:model-value="localValue.selected_traits_id_search_cont_any"
            class="mb-12"
            title="Enkel kenmerk aanwezig"
            @change="update_selected_options"
          />

          <ReportTraitSelect
            v-if="!any_selected"
            v-model:model-value="localValue.selected_traits_id_search_cont_all"
            class="mb-12"
            title="Alle kenmerken aanwezig"
            @change="update_selected_options"
          />

          <v-range-slider
            v-model="localValue.year_of_birth_between"
            hint="Geboortejaar tussen"
            persistent-hint
            :max="2023"
            :min="1922"
            thumb-label="always"
            class="mt-4"
            @update:model-value="update_selected_options"
          ></v-range-slider>
          <v-btn class="mt-2" block bottom color="warning" @click="clear_form"
            >Filters verwijderen</v-btn
          >
          <v-btn
            class="mt-2"
            block
            bottom
            color="primary"
            @click="apply_filters()"
            >Filters toepassen</v-btn
          >
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import countries from "../../assets/countries.json";
import ReportTraitSelect from "../reports/ReportTraitSelect.vue";
import Geocode from "../geocode/Geocode.vue";
import { useAccountCategoriesStoreRefactor } from "@/stores/accountCategoriesStoreRefactor";
import { useAccountLeadsStoreRefactor } from "@/stores/accountLeadsStoreRefactor";

export default {
  name: "AccountFilterDialog",
  components: { ReportTraitSelect, Geocode },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:modelValue",
    "update:show",
    "selected_options",
    "clear_search",
    "search",
    "close_window",
  ],
  data() {
    return {
      any_selected: true,
      localValue: {},
      loadingCategories: false,
      loadingLeads: false,
      error: null,
      // Location search state
      selectedLocation: null,
      currentLocation: null,
      currentLocationName: "",
      searchRadius: 5,
      locationPanel: null,
      gettingLocation: false,
    };
  },
  computed: {
    countries() {
      return countries;
    },
    account_categories() {
      // Use the store instead of local data
      try {
        const accountCategoriesStore = useAccountCategoriesStoreRefactor();
        return accountCategoriesStore.categories || [];
      } catch (error) {
        console.error("Error loading account categories from store:", error);
        return [];
      }
    },
    account_leads() {
      // Use the store instead of local data
      try {
        const accountLeadsStore = useAccountLeadsStoreRefactor();
        return accountLeadsStore.leads || [];
      } catch (error) {
        console.error("Error loading account leads from store:", error);
        return [];
      }
    },
    radiusOptions() {
      return [
        { title: "1 km", value: 1 },
        { title: "5 km", value: 5 },
        { title: "10 km", value: 10 },
        { title: "25 km", value: 25 },
      ];
    },
    isGeolocationActive() {
      return this.currentLocation !== null;
    },
  },
  watch: {
    modelValue: {
      handler(newVal) {
        // Only update if different to avoid recursion
        const newValStr = JSON.stringify(newVal || {});
        const localValStr = JSON.stringify(this.localValue);
        if (newValStr !== localValStr) {
          this.localValue = JSON.parse(newValStr);

          // Re-initialize geolocation UI state when modelValue changes
          this.initializeGeolocationState();

          this.$nextTick(() => {
            this.update_selected_options();
          });
        }
      },
      deep: true,
    },
    localValue: {
      handler(newVal) {
        // Only emit if different to avoid recursion
        const newValStr = JSON.stringify(newVal || {});
        const modelValStr = JSON.stringify(this.modelValue || {});
        if (newValStr !== modelValStr) {
          this.$emit("update:modelValue", newVal);
          this.update_selected_options();
        }
      },
      deep: true,
    },
  },
  created() {
    // Clone the props to avoid direct modification
    this.localValue = JSON.parse(JSON.stringify(this.modelValue || {}));

    // Initialize geolocation UI state from modelValue
    this.initializeGeolocationState();

    // Initialize the selected options count
    this.$nextTick(() => {
      this.update_selected_options();
    });
  },
  async mounted() {
    try {
      this.loadingCategories = true;
      this.loadingLeads = true;
      console.log(
        "AccountFilterDialog mounted - loading categories and leads from store",
      );

      // Use the stores instead of direct API calls
      const accountCategoriesStore = useAccountCategoriesStoreRefactor();
      const accountLeadsStore = useAccountLeadsStoreRefactor();

      // Check if we already have data in the stores
      if (
        accountCategoriesStore.categories.length === 0 ||
        accountLeadsStore.leads.length === 0
      ) {
        console.log("Need to load categories and/or leads");

        // Load categories and leads - wait for both to complete or fail
        const [categoriesResult, leadsResult] = await Promise.allSettled([
          accountCategoriesStore.getAll(),
          accountLeadsStore.getAll(),
        ]);

        if (categoriesResult.status === "fulfilled") {
          console.log(
            "Categories loaded:",
            accountCategoriesStore.categories.length,
          );
        } else {
          console.error("Failed to load categories:", categoriesResult.reason);
        }

        if (leadsResult.status === "fulfilled") {
          console.log("Leads loaded:", accountLeadsStore.leads.length);
        } else {
          console.error("Failed to load leads:", leadsResult.reason);
        }
      } else {
        console.log("Using cached categories and leads");
        console.log(
          "Categories in store:",
          accountCategoriesStore.categories.length,
        );
        console.log("Leads in store:", accountLeadsStore.leads.length);
      }

      // Update filter count
      this.update_selected_options();
    } catch (err) {
      console.error("Error in AccountFilterDialog mounted:", err);
      this.error = err.message;
    } finally {
      this.loadingCategories = false;
      this.loadingLeads = false;
    }
  },
  methods: {
    // Initialize geolocation UI state from modelValue/localValue
    initializeGeolocationState() {
      // Check if we have geolocation parameters in the search
      if (this.localValue.search_lat && this.localValue.search_lng) {
        this.currentLocation = {
          lat: parseFloat(this.localValue.search_lat),
          lng: parseFloat(this.localValue.search_lng),
        };
        this.searchRadius = this.localValue.search_radius || 5;

        // Use stored location name or fallback to generic name
        this.currentLocationName =
          this.localValue.search_location_name || "Geselecteerde locatie";

        // Expand the location panel to show the active location
        this.locationPanel = 0;
      } else {
        // Clear geolocation state if no parameters
        this.currentLocation = null;
        this.currentLocationName = "";
        this.searchRadius = 5;
        this.locationPanel = null;
      }

      // Always reset selectedLocation (this is just for the input field)
      this.selectedLocation = null;
    },

    update_selected_options() {
      // Use a setTimeout to ensure this runs after Vue has processed all data updates
      setTimeout(() => {
        // Instead of using Object.values which can include internal properties,
        // explicitly check only the filter fields we care about
        const filterValues = [
          this.localValue.vip_eq,
          this.localValue.gender_eq,
          this.localValue.mz_eq,
          this.localValue.psychiatrisch_eq,
          this.localValue.country_eq,
          this.localValue.legal_number_cont,
          this.localValue.repat_eq,
          this.localValue.account_lead_id_eq,
          this.localValue.account_category_id_eq,
          this.localValue.selected_traits_id_search_cont_all,
          this.localValue.selected_traits_id_search_cont_any,
          // Only include year_of_birth_between if it's not the default range
          this.isYearRangeNonDefault(this.localValue.year_of_birth_between)
            ? this.localValue.year_of_birth_between
            : null,
          this.localValue.date_of_birth_age_eq,
        ];

        console.log("Filter values to check:", filterValues);

        // Count active filters
        const activeFilters = filterValues.filter(this.compare_values);
        console.log("Active filters:", activeFilters);
        console.log("Active filter count:", activeFilters.length);

        this.$emit("selected_options", activeFilters.length);
      }, 0);
    },

    isYearRangeNonDefault(yearRange) {
      if (!Array.isArray(yearRange)) return false;
      return yearRange[0] !== 1922 || yearRange[1] !== 2023;
    },

    compare_values(n) {
      if (n === undefined || n === null) {
        return false;
      }

      if (Array.isArray(n)) {
        return n.length > 0;
      }

      // Check for empty strings
      if (typeof n === "string") {
        return n.trim() !== "";
      }

      // For boolean values, only true counts as active filter
      if (typeof n === "boolean") {
        return n === true;
      }

      // For numbers, any non-zero value counts
      if (typeof n === "number") {
        return n !== 0;
      }

      return !!n; // Convert to boolean
    },

    resetValuesForDeactivatedSelector() {
      if (this.any_selected) {
        this.localValue.selected_traits_id_search_cont_all = "";
      } else {
        this.localValue.selected_traits_id_search_cont_any = "";
      }
      this.$emit("update:modelValue", this.localValue);
    },

    getLabelForSwitch() {
      if (this.any_selected) {
        return "Enkel kenmerk aanwezig uit selectie";
      }
      return "Alle kenmerken aanwezig uit selectie";
    },

    parseYear(x = 0) {
      return new Date().getFullYear() - x;
    },

    clear_form() {
      // Clear location search when clearing all filters
      this.clearLocationSearch();

      this.$emit("clear_search");
      this.$emit("search");
      this.update_selected_options();
      this.$emit("close_window");
    },

    apply_filters() {
      // Add location parameters if location search is active
      if (this.currentLocation) {
        this.localValue.search_lat = this.currentLocation.lat;
        this.localValue.search_lng = this.currentLocation.lng;
        this.localValue.search_radius = this.searchRadius;
        this.localValue.search_location_name = this.currentLocationName; // Store location name for persistence
      } else {
        // Remove location parameters if not active
        delete this.localValue.search_lat;
        delete this.localValue.search_lng;
        delete this.localValue.search_radius;
        delete this.localValue.search_location_name;
      }

      this.$emit("update:modelValue", this.localValue);
      this.$emit("search");
      this.$emit("close_window");
    },

    // Location search methods
    getCurrentLocation() {
      if (!navigator.geolocation) {
        alert("Geolocatie wordt niet ondersteund door deze browser.");
        return;
      }

      this.gettingLocation = true;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.currentLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.currentLocationName = "Huidige locatie";
          this.gettingLocation = false;

          // Expand location panel if not already expanded
          if (this.locationPanel === null) {
            this.locationPanel = 0;
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Kon locatie niet ophalen. Controleer uw instellingen.");
          this.gettingLocation = false;
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        },
      );
    },

    onLocationSelected(location) {
      if (location && location.lat && location.lng) {
        this.currentLocation = {
          lat: location.lat,
          lng: location.lng,
        };
        this.currentLocationName =
          location.formatted_address || location.description;

        // Expand location panel if not already expanded
        if (this.locationPanel === null) {
          this.locationPanel = 0;
        }
      }
    },

    clearLocationSearch() {
      this.currentLocation = null;
      this.currentLocationName = "";
      this.selectedLocation = null;
      this.searchRadius = 5;
      this.locationPanel = null;

      // Remove location parameters from localValue
      delete this.localValue.search_lat;
      delete this.localValue.search_lng;
      delete this.localValue.search_radius;
      delete this.localValue.search_location_name;

      // Immediately emit the updated value
      this.$emit("update:modelValue", this.localValue);
    },
  },
};
</script>
